import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { toast } from 'sonner';
import { NavBar } from './NavBar';
import { Footer } from './Footer';
import { ToastProvider } from './ToastProvider';
import { ArcadeEffects } from './ArcadeEffects';
import { GameHUD } from './GameHUD';
import { EventBanner } from './EventBanner';
import { registerReferral } from '../../api/referrals';

const REFERRAL_STORAGE_KEY = 'pendingReferralCode';

export const RootLayout: React.FC = () => {
  const { publicKey } = useWallet();
  const location = useLocation();
  const isGameRoom = location.pathname.startsWith('/room/');

  // Capture ?ref= from URL on mount and store in localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      localStorage.setItem(REFERRAL_STORAGE_KEY, refCode);
      // Clean the URL without triggering navigation
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, []);

  // Auto-register referral when wallet connects
  useEffect(() => {
    if (!publicKey) return;
    const pendingCode = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!pendingCode) return;

    const wallet = publicKey.toBase58();
    registerReferral(pendingCode, wallet)
      .then(() => {
        toast.success('Referral applied!');
      })
      .catch(() => {
        // Already referred, invalid code, or self-referral — ignore silently
      })
      .finally(() => {
        localStorage.removeItem(REFERRAL_STORAGE_KEY);
      });
  }, [publicKey]);

  return (
    <ToastProvider>
      <div
        className={`relative flex flex-col ${isGameRoom ? 'h-dvh overflow-hidden' : 'min-h-screen-safe'}`}
      >
        <NavBar />
        {!isGameRoom && <EventBanner />}
        <main
          className={`relative z-10 flex-1 flex flex-col pt-20 sm:pt-16 ${isGameRoom ? 'px-1 pb-0' : 'px-4 pb-16'}`}
        >
          <div
            className={`mx-auto w-full max-w-6xl ${isGameRoom ? 'flex-1 flex flex-col min-h-0' : ''}`}
          >
            <Outlet />
          </div>
        </main>
        {!isGameRoom && <Footer />}
        <ArcadeEffects />
        <GameHUD />
      </div>
    </ToastProvider>
  );
};
